/**
 * « Rejoindre ce projet » — administrateur de la plateforme (décision du 2026-09-30, ARB-SEC-01).
 *
 * L'administrateur VOIT tous les projets de son organisation mais n'y AGIT que par une
 * appartenance réelle : rejoindre en fait un membre propriétaire (`POST /workspaces/{id}/join`),
 * geste tracé à l'audit côté passerelle. Ce hook est le chemin unique des deux surfaces qui le
 * proposent (sélecteur de contexte, portefeuille du tableau de bord) :
 *
 *  1. appeler la passerelle ;
 *  2. ATTENDRE la relecture de `['workspaces']` (et du portefeuille) — sélectionner le projet
 *     avant que la liste ne le déclare accessible le ferait passer pour un lien périmé ;
 *  3. annoncer le résultat (région vivante) et l'exposer pour un affichage visible.
 *
 * Un refus (403, 409, réseau) n'est jamais silencieux : `erreur` porte le message normalisé.
 */
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { api, ApiError, type WorkspaceJoinResult } from '@/api/client'
import { useAnnonce } from '@/components/RegionVivante'
import { useT } from '@/i18n/language'

export interface RejoindreProjet {
  /** Rejoint le projet ; rend le résultat, ou `null` en cas de refus (alors `erreur` est posé). */
  rejoindre: (id: string, nom: string) => Promise<WorkspaceJoinResult | null>
  /** Identifiant du projet en cours de rattachement (bouton occupé), sinon `null`. */
  enCours: string | null
  /** Confirmation visible du dernier rattachement réussi. */
  message: string | null
  /** Refus du dernier essai. */
  erreur: string | null
  effacer: () => void
}

export function useRejoindreProjet(): RejoindreProjet {
  const { t } = useT()
  const queryClient = useQueryClient()
  const annoncer = useAnnonce()
  const [enCours, setEnCours] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const rejoindre = useCallback(async (id: string, nom: string): Promise<WorkspaceJoinResult | null> => {
    setEnCours(id)
    setMessage(null)
    setErreur(null)
    try {
      const resultat = await api.joinWorkspace(id)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
        queryClient.invalidateQueries({ queryKey: ['portfolio'] }),
      ])
      const texte = resultat.joined
        ? t('rejoindre.succes', 'Vous avez rejoint « {{nom}} » en tant que propriétaire (action tracée).', { nom })
        : t('rejoindre.dejaMembre', 'Vous êtes déjà membre de « {{nom}} » ({{role}}) : rien n’a changé.',
          { nom, role: resultat.role })
      setMessage(texte)
      annoncer(texte)
      return resultat
    } catch (e) {
      const detail = e instanceof ApiError ? e.message : String(e)
      const texte = t('rejoindre.echec', 'Impossible de rejoindre « {{nom}} » : {{detail}}', { nom, detail })
      setErreur(texte)
      annoncer(texte)
      return null
    } finally {
      setEnCours(null)
    }
  }, [annoncer, queryClient, t])

  const effacer = useCallback(() => { setMessage(null); setErreur(null) }, [])

  return { rejoindre, enCours, message, erreur, effacer }
}
